// The links a studio's own screens open its stored files through (#431).
//
// Every receipt and studio file is opened through a link the `fileLinks`
// callable minted for this studio, and every such link expires — thirty days
// from the end of the UTC day it was minted in (#497; core/src/file_links.rs
// has the numbers and why). The expiry is in the URL, as `exp`, so this holds
// on to the links it has and decides from each one alone whether it can still
// be handed out:
//
//   * a held link with more than three days left is used as it is, with no
//     round trip — the second open of a receipt is instant;
//   * one with three days or fewer, past its expiry, or with no expiry to read
//     at all (a token or public URL) is replaced before it is used, never after
//     a viewer has failed on it. A six-hour link reads, and always reads as
//     due;
//   * paths asked for in the same tick go to the server as one call, so a
//     screen that wants ten links makes one request, not ten.
//
// ── Offline, and why nothing here is stored ────────────────────────────────
//
// No document holds a URL. Rows hold the object's **path** — `attachments[].path`
// on an expense, `path` on a `files` row — so a cached document offline has
// nothing in it that can go stale. The link is derived from the path when it
// is wanted.
//
// So "renew on reconnect" is the absence of a remembered failure. A request
// that fails offline leaves the cache as it was, and the next open — the first
// one after the connection returns — asks again. One thing is kept through a
// failure on purpose: a link inside its renewal margin but not yet expired is
// still handed out if the renewal cannot be had, since it still opens and a
// refusal would not.
//
// Pure: the server call is passed in (`fetchLinks(paths) → [{ path, url }]`),
// so this runs under test with no Firebase at all.
import { linkExpiresAt, linkNeedsRenewal } from '../domain/fileLinks';

// The callable's own bound (functions/files/links.js, MAX_LINKS_PER_CALL). A
// burst larger than this is split rather than refused.
export const LINKS_PER_CALL = 50;

export const LINK_UNAVAILABLE = 'That file could not be opened just now. Please try again.';

/**
 * A studio's link cache. `linkFor(path)` resolves to a URL that is good for at
 * least the renewal margin, or rejects.
 */
export function createFileLinks({ fetchLinks, now = Date.now }) {
  const held = new Map();
  const inFlight = new Map();
  let batch = null;

  const send = (paths) => {
    const chunks = [];
    for (let i = 0; i < paths.length; i += LINKS_PER_CALL) chunks.push(paths.slice(i, i + LINKS_PER_CALL));
    return Promise.all(chunks.map((chunk) => fetchLinks(chunk))).then((answers) => {
      const byPath = new Map();
      for (const links of answers) {
        for (const link of links || []) {
          if (link && typeof link.path === 'string' && typeof link.url === 'string') byPath.set(link.path, link.url);
        }
      }
      return byPath;
    });
  };

  // Everything asked for before the current tick ends goes in one call.
  const enqueue = (path) => {
    if (!batch) {
      const current = { paths: [] };
      current.promise = Promise.resolve().then(() => {
        batch = null;
        return send(current.paths);
      });
      batch = current;
    }
    if (!batch.paths.includes(path)) batch.paths.push(path);
    return batch.promise;
  };

  const renew = (path) => {
    if (inFlight.has(path)) return inFlight.get(path);
    const request = enqueue(path).then(
      (byPath) => {
        const url = byPath.get(path);
        if (!url) throw new Error(LINK_UNAVAILABLE);
        held.set(path, url);
        return url;
      },
      (err) => {
        // Still good, if not for long: better handed out than refused.
        const stale = held.get(path);
        const expiresAt = stale ? linkExpiresAt(stale) : null;
        if (expiresAt != null && now() < expiresAt) return stale;
        throw err;
      },
    );
    const settled = () => { inFlight.delete(path); };
    request.then(settled, settled);
    inFlight.set(path, request);
    return request;
  };

  return {
    linkFor(path) {
      if (!path) return Promise.resolve(null);
      const current = held.get(path);
      if (current && !linkNeedsRenewal(current, now())) return Promise.resolve(current);
      return renew(path);
    },
    // A path whose object is gone: drop its link so nothing hands it out.
    forget(path) {
      held.delete(path);
    },
  };
}

/**
 * `fetchLinks` over the `fileLinks` callable, or one that refuses with a
 * sentence when callables are not configured.
 */
export function linksFromCallable(call) {
  if (!call) return () => Promise.reject(new Error(LINK_UNAVAILABLE));
  return (paths) => call({ paths }).then((res) => res?.data?.links || []);
}
